import React, { useEffect, useState } from 'react';
import { Mail, Link2, Share2, Check, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { absoluteUrl } from '../../seo/seoConfig.mjs';

const COPY = {
  fr: { share: 'Partager', whatsapp: 'WhatsApp', email: 'Email', copy: 'Copier le lien', copied: 'Lien copi\u00e9', native: 'Partager\u2026', subject: '\u00c0 lire : ', body: 'Je pense que cet article PrimeFace peut t\u2019int\u00e9resser : ', copyError: 'Impossible de copier le lien' },
  en: { share: 'Share', whatsapp: 'WhatsApp', email: 'Email', copy: 'Copy link', copied: 'Link copied', native: 'Share\u2026', subject: 'Worth reading: ', body: 'I think this PrimeFace article may interest you: ', copyError: 'Could not copy the link' },
};

const btn = 'h-9 px-3 bg-[var(--lux-panel)] border-white/10 text-white/80 hover:text-[var(--lux-gold)] hover:border-lux-gold/40 hover:bg-[var(--lux-panel)] transition-[border-color,color] duration-200 text-xs';

/**
 * Share bar for public articles: WhatsApp, email, copy link, native share (when supported).
 * Uses the canonical production URL so shared links always point to primeface.io.
 */
export const ArticleShareBar = ({ title, path, lang, className = '', testIdPrefix = 'article-share' }) => {
  const copy = COPY[lang] || COPY.fr;
  const url = absoluteUrl(path);
  const [canNative, setCanNative] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCanNative(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const text = `${copy.body}${title}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
  const mailto = `mailto:?subject=${encodeURIComponent(`${copy.subject}${title}`)}&body=${encodeURIComponent(`${text}\n\n${url}`)}`;

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else {
        const ta = document.createElement('textarea');
        ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      }
      setCopied(true);
      toast.success(copy.copied);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error(copy.copyError);
    }
  };

  const handleNative = async () => {
    try {
      await navigator.share({ title, text, url });
    } catch (e) {
      if (e?.name !== 'AbortError') handleCopy();
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid={testIdPrefix} aria-label={copy.share}>
      <span className="text-[11px] uppercase tracking-[0.18em] text-white/40 mr-1">{copy.share}</span>
      <Button asChild variant="outline" size="sm" className={btn} data-testid={`${testIdPrefix}-whatsapp`}>
        <a href={whatsapp} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-3.5 h-3.5 mr-1.5" /> {copy.whatsapp}</a>
      </Button>
      <Button asChild variant="outline" size="sm" className={btn} data-testid={`${testIdPrefix}-email`}>
        <a href={mailto}><Mail className="w-3.5 h-3.5 mr-1.5" /> {copy.email}</a>
      </Button>
      <Button type="button" variant="outline" size="sm" className={btn} onClick={handleCopy} data-testid={`${testIdPrefix}-copy`}>
        {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-[var(--lux-gold)]" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />} {copied ? copy.copied : copy.copy}
      </Button>
      {canNative && (
        <Button type="button" size="sm" className="h-9 px-3 bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] text-xs font-semibold" onClick={handleNative} data-testid={`${testIdPrefix}-native`}>
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> {copy.native}
        </Button>
      )}
    </div>
  );
};
